import { inject, Injectable, OnDestroy } from "@angular/core";
import { Title } from "@angular/platform-browser";
import { ActivatedRouteSnapshot, RouterStateSnapshot, TitleStrategy } from "@angular/router";
import { TranslocoService } from "@jsverse/transloco";
import { of, Subject, Subscription } from "rxjs";
import { map, switchMap } from "rxjs/operators";
import { PageTitle } from "src/app/plasma-ui-common/enums/page-title.enum";

/** What a route asked to be called, either as a translation key or as a literal string. */
interface RouteTitle {
  key?: string;
  literal?: string;
}

/**
 * Gives each route its own document title.
 *
 * Every route used to share one title, set once at boot in `AppComponent.seTitle()` — so the
 * results page, the access-verification form, a shared result and the 404 were
 * indistinguishable in the tab strip, in browser history and to a screen reader on
 * navigation (WCAG 2.4.2).
 *
 * A route names itself either with `data.titleKey` (a Transloco key, for anything the app
 * translates) or with the router's own `title` (a literal, used only by the pages whose
 * visible copy is hardcoded English). Routes that declare neither keep the previous
 * app-wide title, which is what keeps this change scoped to the audited routes.
 */
@Injectable({ providedIn: "root" })
export class PageTitleStrategy extends TitleStrategy implements OnDestroy {

  private readonly title = inject(Title);

  private readonly transloco = inject(TranslocoService);

  /** The title of the route being shown now; re-emitted on every navigation. */
  private readonly routeTitle$ = new Subject<RouteTitle>();

  private readonly subscription: Subscription;

  constructor() {
    super();

    // `selectTranslate` emits again once the catalogue has finished loading and on every
    // later language change, so the title is right on a cold load — where a plain
    // `translate()` would return the key — and follows the language picker afterwards.
    this.subscription = this.routeTitle$
      .pipe(
        switchMap(routeTitle => {
          if (!routeTitle.key) {
            return of(routeTitle.literal ?? "");
          }

          return this.transloco.selectTranslate<string>(routeTitle.key).pipe(
            // Transloco echoes the key back when it has no translation for it. Falling back
            // keeps a missing key from putting a dotted path in the browser tab.
            map(translated => (translated === routeTitle.key ? routeTitle.literal ?? "" : translated)),
          );
        }),
      )
      .subscribe(name => this.title.setTitle(this.compose(name)));
  }

  override updateTitle(snapshot: RouterStateSnapshot): void {
    this.routeTitle$.next({
      key: this.deepestTitleKey(snapshot.root),
      literal: this.buildTitle(snapshot),
    });
  }

  ngOnDestroy(): void {
    this.subscription.unsubscribe();
  }

  /**
   * The `titleKey` of the deepest route that declares one.
   *
   * Mirrors how the router resolves `title` itself, so a key on a parent route covers its
   * children until one of them overrides it.
   */
  private deepestTitleKey(root: ActivatedRouteSnapshot): string | undefined {
    let key: string | undefined;
    let route: ActivatedRouteSnapshot | undefined = root;

    while (route) {
      key = route.data?.["titleKey"] ?? key;
      route = route.firstChild ?? undefined;
    }

    return key;
  }

  private compose(name: string): string {
    const pageName = (name ?? "").trim();
    return pageName ? `${pageName} | ${PageTitle.SYNLAB}` : PageTitle.SYNLAB;
  }

}
